import {useBrand} from '@/contexts/BrandContext';
import {usePWA} from '@/hooks/usePWA';
import {useIsMobile} from '@ark/web';
import {
  Close,
  ContentCopy,
  IosShare,
  AddBox,
  CheckCircle,
  ArrowForward,
  GetApp,
  OpenInNew,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Slide,
  Typography,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {TransitionProps} from '@mui/material/transitions';
import {forwardRef, useCallback, useEffect, useMemo, useState} from 'react';

const DISMISSED_KEY = 'ark_pwa_install_guide_dismissed';
const INSTALLED_KEY = 'ark_pwa_installed';

type Platform = 'ios-safari' | 'ios-other' | 'android' | 'desktop';

const detectPlatform = (): Platform => {
  const ua = navigator.userAgent;
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS/.test(ua);

  if (isIOS && isSafari) return 'ios-safari';
  if (isIOS) return 'ios-other';
  if (isAndroid) return 'android';
  return 'desktop';
};

const getBrowserName = (): string => {
  const ua = navigator.userAgent;
  if (/CriOS/.test(ua)) return 'Chrome';
  if (/FxiOS/.test(ua)) return 'Firefox';
  if (/OPiOS/.test(ua)) return 'Opera';
  return 'this browser';
};

type Step = {
  icon: React.ReactNode;
  title: string;
  description: string;
};

const Transition = forwardRef(
  (
    props: TransitionProps & {children: React.ReactElement},
    ref: React.Ref<unknown>,
  ) => <Slide direction="up" ref={ref} {...props} />,
);

const appDescription = (brandName: string) =>
  `${brandName} works as a full app on your phone — get push notifications, quick access from your home screen, and manage your communities on the go.`;

export const InstallGuide = () => {
  const theme = useTheme();
  const {name: brandName, assets} = useBrand();
  const isMobile = useIsMobile('md');
  const {isInstallable, isStandalone, installApp} = usePWA();
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [copied, setCopied] = useState(false);

  const platform = useMemo(() => detectPlatform(), []);
  const browserName = useMemo(() => getBrowserName(), []);

  useEffect(() => {
    if (!isMobile || isStandalone) return;
    if (localStorage.getItem(DISMISSED_KEY) === 'true') return;
    if (localStorage.getItem(INSTALLED_KEY) === 'true') return;

    const timer = setTimeout(() => setOpen(true), 2000);
    return () => clearTimeout(timer);
  }, [isMobile, isStandalone]);

  const handleDismiss = useCallback(() => {
    setOpen(false);
    localStorage.setItem(DISMISSED_KEY, 'true');
  }, []);

  const handleCopyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard not available */
    }
  }, []);

  const handleAndroidInstall = useCallback(async () => {
    if (!isInstallable) return;
    const accepted = await installApp();
    if (accepted) {
      localStorage.setItem(INSTALLED_KEY, 'true');
      setOpen(false);
    }
  }, [isInstallable, installApp]);

  const iosSafariSteps: Step[] = [
    {
      icon: <IosShare sx={{fontSize: 36, color: theme.palette.primary.main}} />,
      title: 'Tap the Share button',
      description:
        'Look for the Share icon at the bottom of your screen (the square with an arrow pointing up).',
    },
    {
      icon: <AddBox sx={{fontSize: 36, color: theme.palette.primary.main}} />,
      title: 'Tap "Add to Home Screen"',
      description:
        'Tap the ellipsis (•••) in the bottom right corner, then tap Share. In the share sheet, tap More to see more options, or scroll down — then tap "Add to Home Screen".',
    },
    {
      icon: (
        <CheckCircle sx={{fontSize: 36, color: theme.palette.success.main}} />
      ),
      title: "Confirm and you're done",
      description: `Tap "Add" in the top right. ${brandName} will appear on your home screen just like a regular app.`,
    },
  ];

  const steps = iosSafariSteps;
  const isLastStep = currentStep === steps.length - 1;

  if (!isMobile || isStandalone) return null;
  if (platform === 'desktop') return null;

  const dialogPaperProps = {
    sx: {
      borderRadius: 4,
      m: 2,
      maxWidth: 360,
      width: '100%',
      overflow: 'hidden',
    },
  };

  if (platform === 'ios-other') {
    return (
      <Dialog
        open={open}
        onClose={handleDismiss}
        TransitionComponent={Transition}
        PaperProps={dialogPaperProps}
      >
        <Box sx={{position: 'relative'}}>
          <IconButton
            onClick={handleDismiss}
            data-attr="install-guide-close"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              color: theme.palette.text.secondary,
            }}
          >
            <Close sx={{fontSize: 20}} />
          </IconButton>
          <Box sx={{p: 3, pt: 4, textAlign: 'center'}}>
            <Box
              component="img"
              src={assets.touchIcon}
              alt={brandName}
              sx={{width: 64, height: 64, borderRadius: 3, mb: 2.5}}
            />
            <Typography sx={{fontSize: 20, fontWeight: 700, mb: 1}}>
              Get the {brandName} App
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                color: theme.palette.text.secondary,
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              {appDescription(brandName)}
            </Typography>
            <Box
              sx={{
                backgroundColor: theme.palette.warning.main + '15',
                borderRadius: 3,
                p: 2,
                mb: 3,
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 0.5,
                }}
              >
                Safari is required on iOS
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: theme.palette.text.secondary,
                  lineHeight: 1.5,
                }}
              >
                {browserName} can't install apps on iPhone. Copy the link below
                and open it in Safari to continue.
              </Typography>
            </Box>
            <Button
              onClick={handleCopyUrl}
              data-attr="install-guide-url-copy"
              variant="contained"
              fullWidth
              startIcon={copied ? <CheckCircle /> : <ContentCopy />}
              sx={{
                borderRadius: 3,
                py: 1.25,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 15,
                mb: 1,
                backgroundColor: copied
                  ? theme.palette.success.main
                  : undefined,
                '&:hover': copied
                  ? {backgroundColor: theme.palette.success.dark}
                  : undefined,
              }}
            >
              {copied ? 'Copied! Now open Safari' : 'Copy Link'}
            </Button>
            {copied && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  mt: 1,
                }}
              >
                <OpenInNew
                  sx={{fontSize: 14, color: theme.palette.text.secondary}}
                />
                <Typography
                  sx={{fontSize: 13, color: theme.palette.text.secondary}}
                >
                  Paste into Safari's address bar
                </Typography>
              </Box>
            )}
            <Button
              onClick={handleDismiss}
              data-attr="install-guide-dismiss"
              fullWidth
              sx={{
                textTransform: 'none',
                color: theme.palette.text.secondary,
                fontSize: 14,
                mt: 1.5,
              }}
            >
              Maybe later
            </Button>
          </Box>
        </Box>
      </Dialog>
    );
  }

  if (platform === 'android') {
    if (!isInstallable) return null;

    return (
      <Dialog
        open={open}
        onClose={handleDismiss}
        TransitionComponent={Transition}
        PaperProps={dialogPaperProps}
      >
        <Box sx={{position: 'relative'}}>
          <IconButton
            onClick={handleDismiss}
            data-attr="install-guide-android-close"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              color: theme.palette.text.secondary,
            }}
          >
            <Close sx={{fontSize: 20}} />
          </IconButton>
          <Box sx={{p: 3, pt: 4, textAlign: 'center'}}>
            <Box
              component="img"
              src={assets.touchIcon}
              alt={brandName}
              sx={{width: 64, height: 64, borderRadius: 3, mb: 2.5}}
            />
            <Typography sx={{fontSize: 20, fontWeight: 700, mb: 1}}>
              Get the {brandName} App
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                color: theme.palette.text.secondary,
                lineHeight: 1.6,
                mb: 3,
              }}
            >
              {appDescription(brandName)}
            </Typography>
            <Button
              onClick={handleAndroidInstall}
              data-attr="install-guide-android-install"
              variant="contained"
              fullWidth
              startIcon={<GetApp />}
              sx={{
                borderRadius: 3,
                py: 1.25,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 15,
                mb: 1.5,
              }}
            >
              Install App
            </Button>
            <Button
              onClick={handleDismiss}
              data-attr="install-guide-android-dismiss"
              fullWidth
              sx={{
                textTransform: 'none',
                color: theme.palette.text.secondary,
                fontSize: 14,
              }}
            >
              Maybe later
            </Button>
          </Box>
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      TransitionComponent={Transition}
      PaperProps={dialogPaperProps}
    >
      <Box sx={{position: 'relative'}}>
        <IconButton
          onClick={handleDismiss}
          data-attr="install-guide-steps-close"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            color: theme.palette.text.secondary,
          }}
        >
          <Close sx={{fontSize: 20}} />
        </IconButton>
        <Box sx={{p: 3, pt: 4, textAlign: 'center'}}>
          <Box
            component="img"
            src={assets.touchIcon}
            alt={brandName}
            sx={{width: 56, height: 56, borderRadius: 2.5, mb: 2}}
          />
          {currentStep === 0 && (
            <Box sx={{mb: 2}}>
              <Typography sx={{fontSize: 20, fontWeight: 700, mb: 1}}>
                Get the {brandName} App
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  color: theme.palette.text.secondary,
                  lineHeight: 1.6,
                }}
              >
                {appDescription(brandName)}
              </Typography>
            </Box>
          )}
          {currentStep > 0 && (
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: theme.palette.text.secondary,
                mb: 0.5,
              }}
            >
              Step {currentStep + 1} of {steps.length}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              justifyContent: 'center',
              mb: 2.5,
            }}
          >
            {steps.map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 24,
                  height: 3,
                  borderRadius: 1.5,
                  backgroundColor:
                    i <= currentStep
                      ? theme.palette.primary.main
                      : theme.palette.grey[300],
                  transition: 'background-color 0.2s',
                }}
              />
            ))}
          </Box>
          <Box sx={{mb: 3}}>
            <Box sx={{display: 'flex', justifyContent: 'center', mb: 2}}>
              {steps[currentStep].icon}
            </Box>
            <Typography sx={{fontSize: 18, fontWeight: 700, mb: 0.75}}>
              {steps[currentStep].title}
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                color: theme.palette.text.secondary,
                lineHeight: 1.5,
              }}
            >
              {steps[currentStep].description}
            </Typography>
          </Box>
          <Box sx={{display: 'flex', gap: 1}}>
            {currentStep > 0 && (
              <Button
                onClick={() => setCurrentStep(s => s - 1)}
                data-attr="install-guide-step-back"
                fullWidth
                sx={{
                  textTransform: 'none',
                  color: theme.palette.text.secondary,
                  fontSize: 14,
                  borderRadius: 3,
                  py: 1,
                }}
              >
                Back
              </Button>
            )}
            {currentStep === 0 && (
              <Button
                onClick={() => setCurrentStep(1)}
                data-attr="install-guide-step-start"
                variant="contained"
                fullWidth
                endIcon={<ArrowForward />}
                sx={{
                  borderRadius: 3,
                  py: 1.25,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 15,
                }}
              >
                Show me how
              </Button>
            )}
            {currentStep > 0 && !isLastStep && (
              <Button
                onClick={() => setCurrentStep(s => s + 1)}
                data-attr="install-guide-step-next"
                variant="contained"
                fullWidth
                endIcon={<ArrowForward />}
                sx={{
                  borderRadius: 3,
                  py: 1.25,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 15,
                }}
              >
                Next
              </Button>
            )}
            {isLastStep && (
              <Button
                onClick={handleDismiss}
                data-attr="install-guide-step-done"
                variant="contained"
                fullWidth
                startIcon={<CheckCircle />}
                sx={{
                  borderRadius: 3,
                  py: 1.25,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 15,
                }}
              >
                Done
              </Button>
            )}
          </Box>
          {currentStep === 0 && (
            <Button
              onClick={handleDismiss}
              data-attr="install-guide-steps-dismiss"
              fullWidth
              sx={{
                textTransform: 'none',
                color: theme.palette.text.secondary,
                fontSize: 14,
                mt: 1,
              }}
            >
              Maybe later
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};
